import { motion } from "framer-motion";

export const Bindi = ({ size = 8, className = "", pulse = false }) => (
  <span
    className={`inline-block shrink-0 rounded-full bg-accent ${pulse ? "animate-bindi" : ""} ${className}`}
    style={{ width: size, height: size }}
  />
);

export const FishMotif = ({ className = "" }) => (
  <svg viewBox="0 0 64 32" fill="none" stroke="currentColor" strokeWidth="1.4" className={className} aria-hidden="true">
    <path d="M4 16 C16 4 32 4 42 16 C32 28 16 28 4 16 Z" />
    <path d="M42 16 L58 6 L54 16 L58 26 Z" />
    <circle cx="13" cy="16" r="1.8" />
    <path d="M21 8.5 Q25 16 21 23.5" />
    <path d="M27 7.5 Q31 16 27 24.5" />
    <path d="M33 8.5 Q37 16 33 23.5" />
  </svg>
);

export const LotusMotif = ({ className = "" }) => (
  <svg viewBox="0 0 48 40" fill="none" stroke="currentColor" strokeWidth="1.4" className={className} aria-hidden="true">
    <path d="M24 4 C21 13 21 21 24 30 C27 21 27 13 24 4 Z" />
    <path d="M24 30 C16 25 11 18 10 9 C18 13 22 20 24 30 Z" />
    <path d="M24 30 C32 25 37 18 38 9 C30 13 26 20 24 30 Z" />
    <path d="M24 30 C13 30 6 25 4 17 C12 19 19 23 24 30 Z" />
    <path d="M24 30 C35 30 42 25 44 17 C36 19 29 23 24 30 Z" />
    <path d="M6 34 Q24 40 42 34" />
  </svg>
);

export const PeacockMotif = ({ className = "" }) => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4" className={className} aria-hidden="true">
    <path d="M30 12 C22 12 16 18 16 26 C16 36 24 42 34 42 C26 38 22 32 22 26 C22 21 25 18 30 18" />
    <path d="M30 12 C33 12 35 14 35 17 C35 20 33 22 30 22 C27 22 25 20 25 17 C25 14 27 12 30 12 Z" />
    <circle cx="30" cy="17" r="1.4" />
    <path d="M30 12 L30 6" />
    <circle cx="30" cy="5" r="1.2" />
    <path d="M27 7 L25 4 M33 7 L35 4" />
    <path d="M34 42 C38 38 40 33 39 27" />
    <path d="M38 40 C42 35 44 29 42 22" />
  </svg>
);

export const SunMotif = ({ className = "" }) => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4" className={className} aria-hidden="true">
    <circle cx="24" cy="24" r="9" />
    <circle cx="24" cy="24" r="3.5" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * 30 * Math.PI) / 180;
      const x1 = 24 + 13 * Math.cos(a);
      const y1 = 24 + 13 * Math.sin(a);
      const x2 = 24 + 19 * Math.cos(a);
      const y2 = 24 + 19 * Math.sin(a);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
    })}
  </svg>
);

export const TreeMotif = ({ className = "" }) => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4" className={className} aria-hidden="true">
    <path d="M24 44 L24 20" />
    <path d="M24 28 C18 26 14 22 13 16" />
    <path d="M24 28 C30 26 34 22 35 16" />
    <path d="M24 20 C20 18 18 14 18 9" />
    <path d="M24 20 C28 18 30 14 30 9" />
    <circle cx="13" cy="13" r="2.4" />
    <circle cx="35" cy="13" r="2.4" />
    <circle cx="24" cy="6" r="2.4" />
    <circle cx="18" cy="7" r="1.6" />
    <circle cx="30" cy="7" r="1.6" />
    <path d="M14 44 Q24 40 34 44" />
  </svg>
);

export const ScallopStrip = ({ className = "" }) => (
  <svg
    viewBox="0 0 240 14"
    preserveAspectRatio="none"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    className={`h-3 w-full ${className}`}
    aria-hidden="true"
  >
    <path d="M0 13 q10 -12 20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0 t20 0" />
  </svg>
);

const FrameCorner = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={`pointer-events-none absolute h-4 w-4 text-accent sm:h-5 sm:w-5 ${className}`} aria-hidden="true">
    <path d="M3 21 V7 Q3 3 7 3 H21" />
    <circle cx="9" cy="9" r="1.7" fill="currentColor" stroke="none" />
  </svg>
);

export const ArtFrame = ({ children, className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="pointer-events-none absolute -inset-2 border border-foreground/45 sm:-inset-3.5" />
    <div className="pointer-events-none absolute -inset-4 border border-foreground/15 sm:-inset-6" />
    <FrameCorner className="-left-1.5 -top-1.5 sm:-left-2 sm:-top-2" />
    <FrameCorner className="-right-1.5 -top-1.5 rotate-90 sm:-right-2 sm:-top-2" />
    <FrameCorner className="-bottom-1.5 -right-1.5 rotate-180 sm:-bottom-2 sm:-right-2" />
    <FrameCorner className="-bottom-1.5 -left-1.5 -rotate-90 sm:-bottom-2 sm:-left-2" />
    {children}
  </div>
);

export const Reveal = ({ children, delay = 0, y = 26, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

// `as` is "h1" when the chapter is shown on its own page rather than within Home.
export const SectionHeading = ({ number, title, note, as: Heading = "h2", className = "" }) => (
  <Reveal className={`mb-14 sm:mb-20 ${className}`}>
    <div className="flex items-end justify-between gap-6 border-b border-border pb-6">
      <div className="flex items-baseline gap-4 sm:gap-6">
        <span className="font-mono text-xs tracking-[0.3em] text-accent">{number}</span>
        <Heading className="font-serif text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">{title}</Heading>
      </div>
      {note && <p className="eyebrow hidden text-muted-foreground sm:block">{note}</p>}
    </div>
  </Reveal>
);
